import { Button, Space, Tag } from 'antd';
import Page from 'components/Page';
import { useMutation } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import useGlobalContext from 'hooks/useGlobalContext';
import useUser from 'hooks/useUser';
import React, { useEffect } from 'react';
import { EDIT_ACCESSIBILITY } from 'shared/constants/permissions';
import BrokenLinksSettings from './BrokenLinksSettings';
import { useAccessibilityReview } from './context';
import { SYNC_ACCESSIBILITY_REVIEW, VERIFY_ACCESSIBILITY_WEBHOOK } from './graphql';
import { DetailField, DetailFields } from 'components/DetailFields';
import { ReviewPage } from './ReviewPage';

const Settings = () => {
  const { scope, refetch } = useAccessibilityReview();
  const { dataSource } = useDataSource();
  const { hasPermission, isSuperUser } = useUser();
  const { message } = useGlobalContext();
  const canManageAccessibility = Boolean(hasPermission(EDIT_ACCESSIBILITY));
  const [verifyWebhook, { loading: verifying }] = useMutation(VERIFY_ACCESSIBILITY_WEBHOOK, {
    onCompleted: async () => {
      message.success('Squidler webhook verified.');
      await refetch();
    },
    onError: error => message.error(error.message),
  });
  const [syncReview, { loading: syncing }] = useMutation(SYNC_ACCESSIBILITY_REVIEW, {
    onCompleted: () => {
      message.success('Squidler import queued.');
      refetch();
    },
    onError: (error) => message.error(error.message),
  });
  const webhookVerified = Boolean(scope?.webhookDeliveries?.length);
  useEffect(() => {
    if (!isSuperUser || !scope || webhookVerified) return undefined;
    const timer = setInterval(() => {
      refetch().catch(() => {});
    }, 4000);
    return () => clearInterval(timer);
  }, [isSuperUser, scope, webhookVerified, refetch]);
  const activationStatus = !scope
    ? 'Not activated'
    : !scope.automaticImportConfigured
      ? 'Automatic import disabled in runtime'
      : webhookVerified
        ? 'Active'
        : scope.automaticRunsEnabled
          ? 'Waiting for webhook verification'
          : 'Ready to configure';

  return (
    <ReviewPage title="Accessibility settings">
      {canManageAccessibility && <BrokenLinksSettings />}
      {isSuperUser && scope && (
        <Page.Content title="Ops">
          <DetailFields style={{ marginBottom: 16 }}>
            <DetailField label="Provider">Squidler</DetailField>
            <DetailField label="Provider site ID">{scope.providerSiteId}</DetailField>
            <DetailField label="Baseline">{`WCAG ${scope.baselineVersion} AA`}</DetailField>
            <DetailField label="Status">
              <Tag color={activationStatus === 'Active' ? 'success' : 'processing'}>{activationStatus}</Tag>
            </DetailField>
            <DetailField label="Webhook URL">
              {scope.webhookUrl || 'Not configured'}
            </DetailField>
          </DetailFields>
          <Space direction="vertical" size="middle">
            <div>
              Register and verify the Squidler callback without starting a scan. Start scans from the Scans page.
            </div>
            <Tag color={webhookVerified ? 'success' : 'default'}>{webhookVerified ? 'Webhook verified' : 'Webhook not yet verified'}</Tag>
            <Button
              type="primary"
              loading={verifying}
              onClick={() => verifyWebhook({ variables: { dataSourceId: dataSource.id } })}
            >
              Verify webhook
            </Button>
            <Button
              loading={syncing}
              onClick={() => syncReview({ variables: { dataSourceId: dataSource.id } })}
            >
              Import from Squidler now
            </Button>
          </Space>
        </Page.Content>
      )}
    </ReviewPage>
  );
};

export default Settings;
